import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { AlertError } from '../actions/shared.actions';
import { ApiService } from '../../services/api/api.service';
import { AssetService } from '../../models/assetservice.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { getOnlySubscribedApps$, getSubscribedAppsMap$ } from '../selectors/apps.selector';

import * as actions from '../actions/shared.actions';

@Injectable()
export class SharedEffects {
    private horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    private verticalPosition: MatSnackBarVerticalPosition = 'bottom';

    constructor(private actions$: Actions, public snackBar: MatSnackBar, private _api: ApiService, private store$: Store) {}

    public alertError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.AlertError),
            mergeMap(action => {
                const config = new MatSnackBarConfig();
                config.verticalPosition = this.verticalPosition;
                config.horizontalPosition = this.horizontalPosition;
                config.duration = action.timeout || 6000;
                config.panelClass = ['failurePanelClass'];
                const message = Array.isArray(action.message) ? action.message.join('<br>') : action.message;
                if (Array.isArray(action.message)) {
                    config.data = message;
                    this.snackBar.open(message, undefined, config);
                } else {
                    this.snackBar.open(message, undefined, config);
                }
                return [];
            })
        );
    });

    public alertSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.AlertSuccess),
            mergeMap(action => {
                const config = new MatSnackBarConfig();
                config.verticalPosition = this.verticalPosition;
                config.horizontalPosition = this.horizontalPosition;
                config.duration = action.timeout || 4000;
                config.panelClass = ['successPanelClass', 'word-break-all'];
                this.snackBar
                    .open(Array.isArray(action.message) ? action.message.join('<br>') : action.message, action.actionText, config)
                    .onAction?.()
                    .subscribe(() => {
                        action?.onActionCallBack();
                    });
                return [];
            })
        );
    });

    getSupportedRegions$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetSupportedRegions),
            mergeMap(() => {
                return this._api.user.getSupportedRegions().pipe(
                    map(response => {
                        return actions.SetSupportedRegions({ supportedRegions: response });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse }));
                    })
                );
            })
        );
    });

    getSubscribedApps$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.CreateNavMap),
            delay(20),
            withLatestFrom(this.store$.select(getOnlySubscribedApps$), this.store$.select(getSubscribedAppsMap$)),
            mergeMap(([_action, apps, appsMap]) => {
                let categories = this.createLeftNavMap(CommonUtilsService.cloneObject(apps));
                categories = this.sortPackages(categories as any, appsMap, true) as any;
                this.store$.dispatch(actions.SetNavMap({ payload: categories as any }));
                return [];
            })
        )
    );

    private createLeftNavMap = (apps: AssetService[]) => {
        const categories = [
            {
                name: 'Transactions',
                id: 'transactions',
                apps: [],
                groups: [],
                packages: [],
                src: '/assets/images/new-ui/transctions_icon_w.png',
                srcHover: '/assets/images/new-ui/transctions_icon.png',
            },
            {
                name: 'Data Preparation',
                id: 'data-preparation',
                apps: [],
                groups: [],
                packages: [],
                icon: 'database',
            },
            {
                name: 'Closures',
                id: 'closures',
                apps: [],
                groups: [],
                packages: [],
                src: '/assets/images/new-ui/closures_icon_w.png',
                srcHover: '/assets/images/new-ui/closures_icon.png',
            },
            {
                name: 'Returns',
                id: 'returns',
                apps: [],
                groups: [],
                packages: [],
                src: '/assets/images/new-ui/returns_icon_w.png',
                srcHover: '/assets/images/new-ui/returns_icon.png',
            },
            {
                name: 'Utilities',
                id: 'utilities',
                apps: [],
                packages: [],
                groups: [],
                icon: 'dynamic_feed',
            },
            {
                name: 'Connectors',
                id: 'connectors',
                src: '',
                srcHover: '',
                apps: [],
                packages: [],
                groups: [],
                icon: 'link',
            },
        ];
        apps.forEach(app => {
            const isPackageApp = app?.assetType === 'PACKAGE';
            let hasPackageTag = app?.tags?.find(tag => tag.tagKey === 'package' && tag.tagValue !== 'ewb') !== undefined;
            app?.tags?.filter(tag => {
                if (hasPackageTag) {
                    return;
                }
                if (tag.tagKey?.toLowerCase() === 'package' && tag.tagValue === 'ewb') {
                    tag.tagValue = 'transactions';
                    hasPackageTag = true;
                } else if (tag.tagKey === 'uitype' && tag.tagValue === 'eninvoice') {
                    tag.tagKey = 'package';
                    tag.tagValue = 'transactions';
                    hasPackageTag = true;
                }
            });
            const packageTags = app?.tags?.filter(tag => tag.tagKey?.toLowerCase() === 'package')?.map(tag => tag.tagValue);
            const packageName = app.displayName;
            if (isPackageApp) {
                if (packageTags?.length > 0) {
                    packageTags?.forEach(tag => {
                        this.pushInCategory(categories, 'PACKAGE', packageName, tag, app);
                    });
                } else {
                    this.pushInCategory(categories, 'PACKAGE', packageName, undefined, app);
                }
            } else if (!isPackageApp && packageTags?.length > 0) {
                packageTags?.forEach(tag => {
                    this.pushInCategory(categories, 'APPS', packageName, tag, app);
                });
            } else {
                this.pushInCategory(categories, 'APPS', undefined, undefined, app);
            }
        });
        return categories;
    };

    private pushInCategory = (categories: any[], sectionName: string, packageName: string, packageTag: string, app: AssetService) => {
        const others = categories.find(categoryObject => categoryObject.id?.toLowerCase() === 'utilities');
        const category = packageTag && categories.find(categoryObject => categoryObject.id?.toLowerCase() === packageTag?.toLowerCase());
        if (category && category.id?.toLowerCase() !== 'others') {
            if (sectionName === 'PACKAGE') {
                this.pushPackageNew(category, packageName, app.serviceId);
            } else {
                this.pushApp(category.apps, app);
            }
        } else if (packageTag) {
            this.pushPackage(others.packages, packageTag, app);
        } else {
            this.pushApp(others.apps, app);
        }
    };

    private pushPackageNew = (category: any, packageName: string, serviceId: string) => {
        category?.packages?.push({
            id: packageName,
            serviceId: serviceId,
        });
    };

    private pushPackage = (
        packages: {
            id: string;
            apps: any[];
            groups: any[];
            src?: string;
            srcHover?: string;
            packages?: undefined;
        }[],
        packageTag: string,
        app: AssetService
    ) => {
        let packageInOthers = packages.find(packageObject => packageObject.id?.toLowerCase() === packageTag?.toLowerCase());
        if (!packageInOthers) {
            packageInOthers = {
                id: packageTag,
                apps: [],
                groups: [],
            };
            packages.push(packageInOthers);
        }
        this.pushApp(packageInOthers.apps, app);
    };

    private pushApp = (apps: { order: number; serviceId: string }[], app: AssetService) => {
        const appTag = app?.tags?.find(tag => tag.tagKey?.toLowerCase() === 'app-order')?.tagValue;
        const appOrder = appTag?.split('--')?.[1];
        apps.push({
            order: appOrder && parseInt(appOrder),
            serviceId: app.serviceId,
        });
    };

    private sortPackages = (
        packages: (
            | {
                  name: string;
                  id: string;
                  apps: any[];
                  groups: any[];
                  src: string;
                  srcHover: string;
                  packages?: undefined;
                  icon?: string;
              }
            | {
                  name: string;
                  id: string;
                  apps: any[];
                  packages: any[];
                  src: string;
                  srcHover?: string;
                  icon?: string;
                  groups: any[];
              }
        )[],
        appsMap: { [property: string]: AssetService },
        dontSort?: boolean
    ) => {
        packages.forEach(packageList => {
            packageList.apps = this.sortApps(packageList.apps, appsMap);
            packageList.groups = this.sortGroups(packageList.groups);
            packageList.packages = packageList.packages && this.sortPackages(packageList.packages, appsMap);
        });
        return !dontSort
            ? packages.sort((a, b) => {
                  const aName = a.id,
                      bName = b.id;
                  return aName.toLowerCase().localeCompare(bName.toLowerCase());
              })
            : packages;
    };

    sortApps = (apps: { serviceId: string; order: number }[], appsMap: { [property: string]: AssetService }) => {
        return apps?.sort((a, b) => {
            const aOrder = a.order,
                bOrder = b.order;
            if (aOrder && bOrder) {
                return aOrder - bOrder;
            } else if (aOrder) {
                return -1;
            } else if (bOrder) {
                return 1;
            }
            const aName = appsMap?.[a.serviceId]?.displayName,
                bName = appsMap?.[b.serviceId]?.displayName;
            return aName?.toLowerCase().localeCompare(bName?.toLowerCase());
        });
    };

    private sortGroups = (groups: { id: string; order: number }[]) => {
        return groups?.sort((a, b) => {
            const aOrder = a.order,
                bOrder = b.order;
            if (aOrder && bOrder) {
                return aOrder - bOrder;
            } else if (aOrder) {
                return -1;
            } else if (bOrder) {
                return 1;
            }
            const aName = a.id,
                bName = b.id;
            return aName.toLowerCase().localeCompare(bName?.toLowerCase());
        });
    };
}
